import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, type DrawerProps, IconButton, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlCheckboxInput } from "Forms/Components/BlCheckboxInput";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type MovableAssetFormType, getMovableAssetFormSchema } from "Forms/Schemas/Assets/movableAssetFormSchema";
import {
  getGetAssetsOverviewQueryKey,
  getListMovableAssetItemsQueryKey,
  useCreateMovableAssetItem,
  useDeleteAssetItem,
  useUpdateMovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type MovableAssetItem, MovableAssetItemMovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { MOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly movableAsset?: MovableAssetItem;
} & DrawerProps;

export const MovableAssetDrawer: FC<Props> = ({ onClose, movableAsset, open, ...rest }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const { t } = useResource();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListMovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateMovableAssetItem();
  const { mutate: update } = useUpdateMovableAssetItem();
  const { mutate: deleteItem } = useDeleteAssetItem();

  const movableAssets: Option[] = (Object.keys(MovableAssetItemMovableSubType) as Array<MovableAssetItemMovableSubType>).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType[MOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const methods = useForm<MovableAssetFormType>({
    resolver: yupResolver(getMovableAssetFormSchema(t)),
    mode: "onBlur",
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        movableAssetType: movableAsset?.movableSubType,
        name: movableAsset?.name,
        value: movableAsset?.amount.value,
        isCoveredByInsurance: movableAsset?.isCoveredByInsurance,
        isUnderFinancing: movableAsset?.isUnderFinancing,
        isUsableForGoals: movableAsset?.isUsableForGoals,
        note: movableAsset?.note ?? undefined,
      };
      reset(defaultValues);
    }
  }, [movableAsset, open, reset]);

  const onSubmit = (data: MovableAssetFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        movableSubType: data.movableAssetType as MovableAssetItemMovableSubType,
        isCoveredByInsurance: !!data.isCoveredByInsurance,
        isUnderFinancing: !!data.isUnderFinancing,
        isUsableForGoals: !!data.isUsableForGoals,
      };

      if (!movableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: movableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: movableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Movable.Deleted));
            invalidate();
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  const movableAssetName = watch("name");

  const onChangeType = (value: string) => {
    if (movableAssetName && movableAssetName.length > 0) return;
    const preFilledName = movableAssets.find((ma) => ma.value === value)?.name;
    setValue("name", preFilledName ?? "");
  };

  return (
    <Drawer anchor="right" open={open} {...rest} onClose={onClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
            <Typography variant="h2">
              {movableAsset ? t(Resources.AnalysisDetail.Assets.Movable.Edit) : t(Resources.AnalysisDetail.Assets.Movable.Add)}
            </Typography>

            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DrawerContent>
            <Stack spacing={Spacing.XL} p={Spacing.XXL}>
              <FormDropdown
                name="movableAssetType"
                options={movableAssets}
                control={control}
                disabled={readOnly}
                labelEndAdornment={<StyledLabelAdornment />}
                label={t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType.Title)}
                errors={errors}
                onChange={onChangeType}
              />
              <BlFormInput
                control={control}
                disabled={readOnly}
                name="name"
                label={t(Resources.AnalysisDetail.Assets.Name)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
              />
              <BlFormInput
                control={control}
                disabled={readOnly}
                name="value"
                label={t(Resources.AnalysisDetail.Assets.Value)}
                labelEndAdornment={<StyledLabelAdornment />}
                errors={errors}
                unmask="typed"
                placeholder="0"
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 0,
                    min: 0,
                  },
                ]}
                inputEndAdornment={
                  <Typography variant="body2" color="secondary">
                    {t(Resources.Common.CZKSuffix)}
                  </Typography>
                }
              />
              <BlFormInput
                disabled={readOnly}
                control={control}
                label={t(Resources.AnalysisDetail.Goals.GoalNote)}
                name="note"
                errors={errors}
                multiline
                minRows={6}
                maxRows={8}
                characterCounter={<FormCharacterCounter characterLimit={NOTE_MAX_LENGTH} name={"note"} />}
              />
              <Divider />
              <Typography fontWeight={600} variant="subtitle1">
                {t(Resources.AnalysisDetail.Assets.Attributes)}
              </Typography>
              <BlCheckboxInput
                control={control}
                disabled={readOnly}
                name="isUsableForGoals"
                text={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
                errors={errors}
              />
              <BlCheckboxInput
                control={control}
                disabled={readOnly}
                name="isCoveredByInsurance"
                text={t(Resources.AnalysisDetail.Assets.IsCoveredByInsurance)}
                errors={errors}
              />
              <BlCheckboxInput
                control={control}
                disabled={readOnly}
                name="isUnderFinancing"
                text={t(Resources.AnalysisDetail.Assets.IsUnderFinancing)}
                errors={errors}
              />
            </Stack>
          </DrawerContent>

          <Box display="flex" alignItems="center" justifyContent="space-between" py={Spacing.XXL} px={Spacing.XXL}>
            <Box>
              {!!movableAsset && (
                <Button
                  variant="text"
                  color="error"
                  disabled={readOnly}
                  sx={{
                    marginRight: Spacing.XL,
                  }}
                  onClick={onDelete}
                >
                  {t(Resources.Common.Delete)}
                </Button>
              )}
            </Box>

            <Box display="flex" flexDirection="row" justifyContent="flex-end">
              <Button
                onClick={onClose}
                color="secondary"
                sx={{
                  marginRight: Spacing.XL,
                }}
              >
                {t(Resources.Common.Close)}
              </Button>
              <Button disabled={readOnly} type="submit">
                {movableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Drawer>
  );
};
